import React, { useMemo, useState } from "react"
import { useStateMachine } from "../../../context/stateMachine/StateMachineContext"
import { Steps } from "./step-library"
import { Container, FlexContainer, H2 } from "@carrotfertility/carotene"
import ButtonSelectGroup from "../../shared/ButtonSelectGroup"
import useButtonSingleSelect from "../../hooks/useButtonSingleSelect"
import { BodySmall } from "components/views/atoms/Bodies"
import { useRoutingInfo } from "components/carrot-plans/context/RoutingInfoContext"
import ArrowSubmitButton from "components/carrot-plans/shared/ArrowSubmitButton"
import { useSelector } from "react-redux"
import { getBenefitConfiguration } from "reducers/benefitConfiguration"
import { useJourneyFlow } from "../../shared/JourneyFlowContainer"
import { ClickExpandStyledInputBox, ClickExpandStyledInput } from "../../../views/atoms/forms/StyledInputs"
import styled from "styled-components"

const ViewName = Steps.WHAT_KIND_OF_JOURNEY

type SomethingElseInputTitleProps = { loading: boolean }
const SomethingElseInputTitle = styled.p<SomethingElseInputTitleProps>`
  margin-bottom: 8px;
  padding-left: 24px;
  font-size: 16px;
  color: ${(props) => (props.loading ? "#CCCCCC" : "#000000")};
`

const SomethingElseInputBox = styled(ClickExpandStyledInputBox)((props: { loading: boolean }) => ({
  color: props.loading && "#CCCCCC",
  cursor: props.loading && "none",
  background: props.loading && "#F5F5F5",
  border: props.loading && "2px solid #CCCCCC",
  marginTop: "4px"
}))

const SomethingElseInput = styled(ClickExpandStyledInput)`
  color: ${(props) => props.disabled && "#CCCCCC"};
`

function useJourneyOptions(journey): { options: { title: string; id: string }[] } {
  const { featureConfig } = useSelector(getBenefitConfiguration)
  const options = useMemo(
    () =>
      [
        { title: "Adoption", id: "ADOPTION" },
        { title: "Working with a gestational carrier", id: "GESTATIONAL" },
        { title: "Fertility preservation (e.g., freezing eggs or sperm)", id: "PRESERVATION" },
        { title: "Assisted reproduction (e.g., IUI or IVF)", id: "ASSISTED_REPRODUCTION" },
        { title: "Trying to get pregnant (e.g., timed intercourse)", id: "TRY_PREGNANT" },
        { title: "Exploring my options", id: "EXPLORING" },
        { title: "Currently pregnant", id: "PREGNANT" },
        { title: "Something else", id: "SOMETHING_ELSE" }
      ].filter(({ id }) => {
        if (!featureConfig?.isAdoptionEnabled && id === "ADOPTION") {
          return false
        }
        if (!featureConfig?.isGcEnabled && id === "GESTATIONAL") {
          return false
        }
        if (!featureConfig?.isCarrotPregnancyEnabled && id === "PREGNANT") {
          return false
        }
        if (id === "SOMETHING_ELSE" && journey === "SOMETHING_ELSE") {
          return false
        }
        return true
      }),
    [featureConfig.isAdoptionEnabled, featureConfig.isGcEnabled, featureConfig.isCarrotPregnancyEnabled, journey]
  )

  return {
    options
  }
}

export default function WhatKindOfJourneyStep(): JSX.Element {
  const { updateRoutingInfo, routingData } = useRoutingInfo()
  const { handleContinueLoading, continueLoading } = useJourneyFlow()
  const [selectedJourney, setSelectedJourney] = useState(routingData?.journey)
  const { setNextStep } = useStateMachine(ViewName, selectedJourney)

  const [loading, setLoading] = useState(false)

  const [somethingElseDetails, setSomethingElseDetails] = useState(routingData?.somethingElseJourney || "")
  const [showSomethingElseToggle, setShowSomethingElseToggle] = useState(selectedJourney === "SOMETHING_ELSE")

  async function onClickJourney(selectedValue: string): Promise<void> {
    if (selectedValue === "SOMETHING_ELSE") {
      setSelectedJourney(selectedValue)
      setShowSomethingElseToggle(true)
    } else if (selectedValue !== null) {
      setSelectedJourney(selectedValue)
      setShowSomethingElseToggle(false)
      await handleContinueLoading(async () => {
        await updateRoutingInfo({ journey: selectedValue, somethingElseJourney: null })
      })
      setNextStep(ViewName, selectedValue)
    }
  }

  async function onClickContinue(): Promise<void> {
    if (selectedJourney === "SOMETHING_ELSE") {
      setLoading(true)
      await handleContinueLoading(async () => {
        await updateRoutingInfo({ journey: selectedJourney, somethingElseJourney: somethingElseDetails })
      })
      setNextStep(ViewName, selectedJourney)
    }
  }

  const { options: journeyOptions } = useJourneyOptions(selectedJourney)
  const { valid, ...buttonProps } = useButtonSingleSelect({
    options: journeyOptions,
    defaultValue: selectedJourney,
    onSubmit: onClickJourney
  })

  return (
    <form
      onSubmit={(event) => {
        event.preventDefault()
        onClickContinue()
      }}
    >
      <H2 stack="tiny">What kind of journey are you on or most interested in?</H2>
      <Container stack="tiny">
        <BodySmall>This is just for now. Keep us updated so we can continue to offer the best support.</BodySmall>
      </Container>
      <Container stack="large">
        <BodySmall>
          {`Also, know that we always respect local laws and regulations. We'll help you access care domestically or across
        borders, as appropriate.`}
        </BodySmall>
      </Container>
      <ButtonSelectGroup {...buttonProps} loading={loading || buttonProps.loading} />
      {showSomethingElseToggle && (
        <SomethingElseInputBox loading={loading}>
          <SomethingElseInputTitle loading={loading}>Something else</SomethingElseInputTitle>
          <SomethingElseInput
            id="somethingElseDetails"
            placeholder="Optional details"
            value={somethingElseDetails}
            onChange={(evt) => setSomethingElseDetails(evt.target.value)}
            onClick={(e) => e.stopPropagation()}
            autoFocus
            maxLength={255}
            disabled={loading}
          />
          {somethingElseDetails && somethingElseDetails.length >= 255 * 0.75 && (
            <div className="flex flex-row-reverse justify-between items-center" style={{ margin: "4px 24px 0px 24px" }}>
              <p className="black-40">{somethingElseDetails.length}/255</p>
            </div>
          )}
        </SomethingElseInputBox>
      )}
      <FlexContainer paddingTopBottom="huge" justifyContent="flex-end">
        <ArrowSubmitButton enabled={valid && !continueLoading}>Continue</ArrowSubmitButton>
      </FlexContainer>
    </form>
  )
}
